import {Transition, TransitionChild} from "@headlessui/react";
import {type PropsWithChildren, useCallback, useEffect, useLayoutEffect, useRef} from "react";
import {createPortal} from "react-dom";
import {Outlet, useNavigate} from "react-router-dom";
import clsx from "clsx";
import {CloseCircle} from "@chayka/icons";
import {registerBottomSheetCloseHandler} from "../bottomSheetRegistry";

const SNAPSHOT_TTL_MS = 1500;
const FULL_HEIGHT_BOTTOM_SHEET_ATTR = "data-full-height-bottom-sheet-open-count";
const BOTTOM_SHEET_OPEN_ATTR = "data-bottom-sheet-open";
const PAGE_SHELL_SCROLL_STATE_KEY = "__pageShellScrollPosition";

interface PageShellScrollPosition {
  top: number;
  left: number;
}

let routeSnapshotCache: {
  node: HTMLDivElement;
  capturedAt: number;
  pageShellScrollPosition: PageShellScrollPosition;
} | null = null;

const clearSnapshotIds = (element: HTMLElement) => {
  element.removeAttribute("id");

  for (const child of element.querySelectorAll<HTMLElement>("[id]")) {
    child.removeAttribute("id");
  }
};

const copyScrollPositions = (source: HTMLElement, snapshot: HTMLElement) => {
  if (source.scrollTop !== 0) {
    snapshot.scrollTop = source.scrollTop;
  }

  if (source.scrollLeft !== 0) {
    snapshot.scrollLeft = source.scrollLeft;
  }

  const sourceChildren = source.children;
  const snapshotChildren = snapshot.children;

  for (let index = 0; index < sourceChildren.length; index += 1) {
    const sourceChild = sourceChildren.item(index);
    const snapshotChild = snapshotChildren.item(index);

    if (!(sourceChild instanceof HTMLElement) || !(snapshotChild instanceof HTMLElement)) {
      continue;
    }

    copyScrollPositions(sourceChild, snapshotChild);
  }
};

const getPageShellScrollPosition = (): PageShellScrollPosition => {
  const pageShell = document.querySelector<HTMLElement>(".app-page-shell");

  if (pageShell) {
    return {
      top: pageShell.scrollTop,
      left: pageShell.scrollLeft,
    };
  }

  return {
    top: window.scrollY,
    left: window.scrollX,
  };
};

const applyPageShellScrollPosition = (
  root: ParentNode,
  scrollPosition: PageShellScrollPosition,
) => {
  const pageShell = root.querySelector<HTMLElement>(".app-page-shell");

  if (!pageShell) {
    window.scrollTo(scrollPosition.left, scrollPosition.top);
    return;
  }

  pageShell.scrollTop = scrollPosition.top;
  pageShell.scrollLeft = scrollPosition.left;
};

const captureRouteSnapshot = () => {
  const root = document.getElementById("root");

  if (!root) {
    return;
  }

  const snapshot = root.cloneNode(true) as HTMLDivElement;
  const pageShellScrollPosition = getPageShellScrollPosition();

  clearSnapshotIds(snapshot);
  copyScrollPositions(root, snapshot);
  applyPageShellScrollPosition(snapshot, pageShellScrollPosition);
  snapshot.setAttribute("aria-hidden", "true");
  snapshot.setAttribute("data-bottom-sheet-route-snapshot", "true");
  snapshot.style.height = "100%";
  snapshot.style.pointerEvents = "none";

  routeSnapshotCache = {node: snapshot, capturedAt: Date.now(), pageShellScrollPosition};
};

if (typeof window !== "undefined") {
  const historyState = window.history as History & {
    __bottomSheetSnapshotPatched__?: boolean;
  };

  if (!historyState.__bottomSheetSnapshotPatched__) {
    const originalPushState = window.history.pushState.bind(window.history);
    const originalReplaceState = window.history.replaceState.bind(window.history);

    const persistCurrentPageShellScroll = () => {
      const currentState = window.history.state ?? {};

      originalReplaceState(
        {
          ...currentState,
          [PAGE_SHELL_SCROLL_STATE_KEY]: getPageShellScrollPosition(),
        },
        "",
      );
    };

    window.history.pushState = ((...args) => {
      persistCurrentPageShellScroll();
      captureRouteSnapshot();
      return originalPushState(...args);
    }) as History["pushState"];

    window.history.replaceState = ((...args) => {
      const [state, unused, url] = args;

      captureRouteSnapshot();

      return originalReplaceState(
        {
          ...(typeof state === "object" && state !== null ? state : {}),
          [PAGE_SHELL_SCROLL_STATE_KEY]: getPageShellScrollPosition(),
        },
        unused,
        url,
      );
    }) as History["replaceState"];

    historyState.__bottomSheetSnapshotPatched__ = true;
  }
}

interface BottomSheetProps extends PropsWithChildren {
  isOpen: boolean;
  onClose?: () => void;
  onBeforeClose?: () => boolean;
  fullHeight?: boolean;
  afterLeave?: () => void;
  showRouteSnapshot?: boolean;
}

export const BottomSheet: React.FC<BottomSheetProps> = ({
  children,
  isOpen,
  onClose,
  onBeforeClose,
  fullHeight,
  afterLeave,
  showRouteSnapshot,
}) => {
  const portalRoot = typeof document !== "undefined" ? document.body : null;
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const backgroundRef = useRef<HTMLDivElement | null>(null);
  const shouldAnimateOnUnmountRef = useRef(false);
  const fullHeightVisibilityAppliedRef = useRef(false);
  const shouldRenderRouteSnapshot = !onClose || showRouteSnapshot;

  const setFullHeightPageHidden = useCallback((shouldHide: boolean) => {
    if (typeof document === "undefined") {
      return;
    }

    if (shouldHide === fullHeightVisibilityAppliedRef.current) {
      return;
    }

    const activeCount = Number(document.body.getAttribute(FULL_HEIGHT_BOTTOM_SHEET_ATTR) ?? "0");
    const nextCount = shouldHide ? activeCount + 1 : Math.max(0, activeCount - 1);

    fullHeightVisibilityAppliedRef.current = shouldHide;

    if (nextCount === 0) {
      document.body.removeAttribute(FULL_HEIGHT_BOTTOM_SHEET_ATTR);
      return;
    }

    document.body.setAttribute(FULL_HEIGHT_BOTTOM_SHEET_ATTR, String(nextCount));
  }, []);

  const animateDetachedClose = useCallback(() => {
    if (!containerRef.current) {
      return;
    }

    const snapshot = containerRef.current.cloneNode(true) as HTMLDivElement;
    const backdrop = snapshot.querySelector<HTMLElement>("[data-bottom-sheet-backdrop]");
    const panel = snapshot.querySelector<HTMLElement>("[data-bottom-sheet-panel]");
    const snapshotScrollArea = snapshot.querySelector<HTMLElement>("[data-bottom-sheet-scroll]");
    const sourceScrollArea = containerRef.current.querySelector<HTMLElement>(
      "[data-bottom-sheet-scroll]",
    );

    snapshot.style.pointerEvents = "none";
    snapshot.setAttribute("aria-hidden", "true");

    if (sourceScrollArea && snapshotScrollArea) {
      snapshotScrollArea.scrollTop = sourceScrollArea.scrollTop;
    }

    if (backdrop) {
      backdrop.style.opacity = "1";
      backdrop.style.transition = "opacity 200ms ease-in";
    }

    if (panel) {
      panel.style.opacity = "1";
      panel.style.transform = "translateY(0)";
      panel.style.transition = "transform 300ms ease-in, opacity 300ms ease-in";
      panel.style.willChange = "transform, opacity";
    }

    document.body.append(snapshot);

    requestAnimationFrame(() => {
      if (backdrop) {
        backdrop.style.opacity = "0";
      }

      if (panel) {
        panel.style.opacity = "0";
        panel.style.transform = "translateY(100%)";
      }
    });

    window.setTimeout(() => {
      snapshot.remove();
    }, 320);
  }, []);

  useLayoutEffect(() => {
    if (!shouldRenderRouteSnapshot || !backgroundRef.current || !routeSnapshotCache) {
      return;
    }

    if (Date.now() - routeSnapshotCache.capturedAt > SNAPSHOT_TTL_MS) {
      return;
    }

    const snapshot = routeSnapshotCache.node.cloneNode(true) as HTMLDivElement;
    const {pageShellScrollPosition} = routeSnapshotCache;

    backgroundRef.current.replaceChildren(snapshot);

    const restoreSnapshotScrollPosition = () => {
      applyPageShellScrollPosition(snapshot, pageShellScrollPosition);
    };

    restoreSnapshotScrollPosition();
    requestAnimationFrame(restoreSnapshotScrollPosition);

    return () => {
      snapshot.remove();
    };
  }, [shouldRenderRouteSnapshot]);

  useLayoutEffect(() => {
    return () => {
      if (!onClose && isOpen && shouldAnimateOnUnmountRef.current) {
        animateDetachedClose();
      }
    };
  }, [animateDetachedClose, isOpen, onClose]);

  const handleClose = useCallback(() => {
    if (onBeforeClose && !onBeforeClose()) {
      return;
    }

    if (onClose) {
      onClose();
      return;
    }

    if (fullHeight) {
      setFullHeightPageHidden(false);
    }

    shouldAnimateOnUnmountRef.current = true;
    navigate(-1);
  }, [fullHeight, navigate, onBeforeClose, onClose, setFullHeightPageHidden]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.setAttribute(BOTTOM_SHEET_OPEN_ATTR, "true");

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.removeAttribute(BOTTOM_SHEET_OPEN_ATTR);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    return registerBottomSheetCloseHandler(handleClose);
  }, [handleClose, isOpen]);

  useEffect(() => {
    if (fullHeight) {
      return;
    }

    setFullHeightPageHidden(false);
  }, [fullHeight, setFullHeightPageHidden]);

  useEffect(() => {
    return () => {
      setFullHeightPageHidden(false);
    };
  }, [setFullHeightPageHidden]);

  const handleAfterEnter = useCallback(() => {
    if (!fullHeight) {
      return;
    }

    setFullHeightPageHidden(true);
  }, [fullHeight, setFullHeightPageHidden]);

  const handleBeforeEnter = useCallback(() => {
    if (fullHeight && shouldRenderRouteSnapshot) {
      setFullHeightPageHidden(true);
      return;
    }

    setFullHeightPageHidden(false);
  }, [fullHeight, setFullHeightPageHidden, shouldRenderRouteSnapshot]);

  const handleBeforeLeave = useCallback(() => {
    setFullHeightPageHidden(false);
  }, [setFullHeightPageHidden]);

  if (!portalRoot) {
    return null;
  }

  return createPortal(
    <Transition
      appear
      show={isOpen}
      afterEnter={handleAfterEnter}
      afterLeave={afterLeave}
      beforeEnter={handleBeforeEnter}
      beforeLeave={handleBeforeLeave}
    >
      <div ref={containerRef} className="fixed inset-0 max-w-160 mx-auto" style={{zIndex: 1000}}>
        <div
          ref={backgroundRef}
          aria-hidden="true"
          className="absolute inset-0 overflow-hidden pointer-events-none"
        />
        <TransitionChild
          enter="transition duration-300 ease-out"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition duration-200 ease-in"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            aria-hidden="true"
            className="absolute inset-0 bg-black/40"
            data-bottom-sheet-backdrop
            onClick={handleClose}
          />
        </TransitionChild>

        <div className="pointer-events-none absolute inset-0 flex items-end justify-center">
          <TransitionChild
            enter="transform transition duration-300 ease-out"
            enterFrom="translate-y-full opacity-0"
            enterTo="translate-y-0 opacity-100"
            leave="transform transition duration-300 ease-in"
            leaveFrom="translate-y-0 opacity-100"
            leaveTo="translate-y-full opacity-0"
          >
            <div
              aria-modal="true"
              data-bottom-sheet-panel
              className={clsx(
                "relative pointer-events-auto flex w-full flex-col overflow-hidden bg-background shadow-2xl",
                fullHeight
                  ? "h-full max-h-full max-w-none rounded-none pt-0"
                  : "max-h-[min(85vh,calc(100vh-1rem))] max-w-3xl rounded-t-[28px] pt-3",
              )}
              onClick={(event) => event.stopPropagation()}
              role="dialog"
            >
              <div
                className="min-h-0 flex-1 overflow-y-auto p-6 pb-[calc(env(safe-area-inset-bottom)+2rem)] overscroll-none"
                data-bottom-sheet-scroll
              >
                <div
                  onClick={handleClose}
                  className="absolute right-3.5 top-3.5 rounded-full p-1 text-controls-gray disabled:pointer-events-none data-[state=open]:bg-gray-100 z-10"
                >
                  <CloseCircle size={30} />
                </div>
                {children ?? <Outlet />}
              </div>
            </div>
          </TransitionChild>
        </div>
      </div>
    </Transition>,
    portalRoot,
  );
};
